import { api } from "@/lib/api/client";
import { apiGet, apiPost, apiPostFormData } from "../_apiShim";

const BASE_PATH = "/api/v1/s3/";

export async function getUrls(eventId: string): Promise<string[]> {
  return await apiGet<string[]>(
    `${BASE_PATH}${eventId}/urls`,
    { "Cache-Control": "no-store" },
    true,
  );
}

/**
 * Shared library only — no event scope. Used by template-library editors
 * (email/website/document) where there is no eventId in the route.
 */
export async function getSharedUrls(): Promise<string[]> {
  return await apiGet<string[]>(
    "/api/v1/shared-assets/urls",
    {},
    true,
  );
}

export async function uploadTitleImage(eventId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append("file", file);
  await apiPostFormData<void>(`${BASE_PATH}${eventId}/upload-title-image`, formData, {
    "Cache-Control": "no-store",
  });
}

// Server-side mirror values from R2UploadGuard.java. Pre-validation here is
// purely UX (fail fast, avoid a roundtrip on obviously-bad input). The server
// re-validates before signing and R2 enforces signed contentType +
// contentLength at PUT time, so this client check cannot be used to skip
// validation — only to surface errors before hitting the network.
const ACCEPTED_IMAGE_TYPES: ReadonlySet<string> = new Set(["image/jpeg", "image/png", "image/gif"]);
const MAX_IMAGE_SIZE_BYTES = 2.5 * 1024 * 1024;
const MAX_FILES_PER_UPLOAD = 10;

type PresignImageUploadResponse = {
  uploadUrl: string;
  publicUrl: string;
  s3Key: string;
};

/**
 * Returns N presigned PUT URLs for direct browser → R2 upload. The backend
 * validates each file's contentType + size against R2UploadGuard caps and
 * binds those values into the signature. R2 rejects mismatched PUTs with 403.
 */
export async function getPresignedImageUploadUrls(
  eventId: string,
  files: { fileName: string; contentType: string; fileSize: number }[],
): Promise<PresignImageUploadResponse[]> {
  return await apiPost<PresignImageUploadResponse[]>(
    `${BASE_PATH}${eventId}/presign-image-uploads`,
    { files },
    { "Cache-Control": "no-store" },
    true,
  );
}

async function uploadImagesViaPresign(
  presignPath: string,
  files: File[],
): Promise<string[]> {
  if (files.length === 0) return [];
  if (files.length > MAX_FILES_PER_UPLOAD) {
    throw new Error(`Too many files; max ${MAX_FILES_PER_UPLOAD} per request`);
  }
  for (const f of files) {
    if (!ACCEPTED_IMAGE_TYPES.has(f.type)) {
      throw new Error("Image must be JPEG, PNG or GIF");
    }
    if (f.size > MAX_IMAGE_SIZE_BYTES) {
      throw new Error("Image must not exceed 2.5 MB");
    }
  }

  const presigned = await apiPost<PresignImageUploadResponse[]>(
    presignPath,
    { files: files.map((f) => ({ fileName: f.name, contentType: f.type, fileSize: f.size })) },
    { "Cache-Control": "no-store" },
    true,
  );

  await Promise.all(
    presigned.map(async ({ uploadUrl }, i) => {
      const file = files[i];
      const r = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!r.ok) {
        throw new Error(`R2 upload failed (${r.status}) for ${file.name}`);
      }
    }),
  );

  return presigned.map((p) => p.publicUrl);
}

export async function uploadImages(eventId: string, files: File[]): Promise<string[]> {
  return uploadImagesViaPresign(`${BASE_PATH}${eventId}/presign-image-uploads`, files);
}

/**
 * Template-library counterpart of {@link uploadImages}: keys land under
 * public-assets/<templateId>/ (backend TemplateAssetsResource, tenant-gated
 * by templateId). Used by the template-mode JSON-import asset re-upload.
 */
export async function uploadTemplateImages(templateId: string, files: File[]): Promise<string[]> {
  return uploadImagesViaPresign(`${BASE_PATH}template/${templateId}/presign-image-uploads`, files);
}

/** Image library of a template-library template (own uploads + shared). */
export async function getTemplateUrls(templateId: string): Promise<string[]> {
  return await apiGet<string[]>(
    `${BASE_PATH}template/${templateId}/urls`,
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function deleteImages(eventId: string, imageNames: string[]): Promise<void> {
  return await apiPost<void>(
    `${BASE_PATH}${eventId}/delete-images`,
    imageNames,
    { "Cache-Control": "no-store" },
  );
}

/**
 * Uploads a 512x512 PNG image and creates favicon in all required sizes.
 * Returns the URL for the 32x32 favicon.
 */
export async function uploadFavicon(eventId: string, faviconFile: File): Promise<string> {
  if (!faviconFile.type.includes("png")) {
    throw new Error("Favicon must be a PNG image");
  }
  const formData = new FormData();
  formData.append("favicon", faviconFile);
  const data = await apiPostFormData<string>(
    `${BASE_PATH}${eventId}/favicon`,
    formData,
    { "Cache-Control": "no-store" },
    { responseType: "text" },
  );
  return data.replaceAll('"', "");
}

export async function getFavicon32Url(eventId: string): Promise<string> {
  return await apiGet<string>(`${BASE_PATH}${eventId}/favicon`, {
    "Cache-Control": "no-store",
  });
}

export async function copyImageToTitleImage(eventId: string, imageName: string): Promise<string> {
  return await apiPost<string>(
    `${BASE_PATH}${eventId}/copy-to-title-image`,
    { imageName },
    { "Cache-Control": "no-store" },
  );
}

export async function getEventImageUrl(eventId: string): Promise<string> {
  try {
    const r = await api.get<string>(`${BASE_PATH}${eventId}/event-image`, {
      headers: { "Cache-Control": "no-store" },
      responseType: "text",
    });
    return r.data || "";
  } catch {
    return "";
  }
}

export async function uploadOgImage(eventId: string, file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const data = await apiPostFormData<string>(
    `${BASE_PATH}${eventId}/og-image`,
    formData,
    { "Cache-Control": "no-store" },
    { responseType: "text" },
  );
  return data.replace(/"/g, "");
}

export async function getOgImageUrls(eventId: string): Promise<string[]> {
  return await apiGet<string[]>(
    `${BASE_PATH}${eventId}/og-images`,
    { "Cache-Control": "no-store" },
    true,
  );
}

/**
 * Gets presigned URLs for multiple events' title images.
 */
export async function getEventImageUrls(eventIds: string[]): Promise<Record<string, string>> {
  return await apiPost<Record<string, string>>(
    `${BASE_PATH}event-images`,
    eventIds,
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function getPresignedVideoUploadUrl(
  eventId: string,
  fileName: string,
  contentType: string,
  fileSize: number,
): Promise<{ uploadUrl: string; publicUrl: string }> {
  return await apiPost<{ uploadUrl: string; publicUrl: string }>(
    `${BASE_PATH}${eventId}/presign-video-upload`,
    { fileName, contentType, fileSize },
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function getVideoUrls(eventId: string): Promise<string[]> {
  return await apiGet<string[]>(
    `${BASE_PATH}${eventId}/video-urls`,
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function deleteVideos(eventId: string, videoNames: string[]): Promise<void> {
  return await apiPost<void>(
    `${BASE_PATH}${eventId}/delete-videos`,
    videoNames,
    { "Cache-Control": "no-store" },
  );
}
